import React, { useEffect } from 'react';
import { ActionIcon, Flex, Text, UnstyledButton } from '@mantine/core';
import { useMutation } from '@apollo/client';
import { IconArrowRight, IconTag, IconX } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

import { Notification } from '../../codegen/graphql';
import formatDate from '../../utility/formatDate';
import {
    DELETE_NOTIFICATION,
    GET_NOTIFICATIONS,
    GET_UNREAD_NOTIFICATION_COUNT,
    MARK_NOTIFICATION_AS_READ,
} from '../../apollo/mutations/notification';
import { showErrorNotification } from '../../notifications';

type Props = {
    notification: Notification;
    close: () => void;
    removeNotification: (id: string) => void;
};

function NotificationItem({ notification, close, removeNotification }: Props) {
    const navigate = useNavigate();

    const [markNotificationAsRead, { error: readError }] = useMutation(
        MARK_NOTIFICATION_AS_READ,
        {
            variables: {
                id: notification.id,
            },
            refetchQueries: [GET_NOTIFICATIONS, GET_UNREAD_NOTIFICATION_COUNT],
        },
    );

    const [deleteNotification, { error: deleteError }] = useMutation(
        DELETE_NOTIFICATION,
        {
            variables: {
                id: notification.id,
            },
            refetchQueries: [GET_NOTIFICATIONS, GET_UNREAD_NOTIFICATION_COUNT],
        },
    );

    useEffect(() => {
        if (readError) {
            showErrorNotification('Error marking notification as read');
        }
        if (deleteError) {
            showErrorNotification('Error deleting notification');
        }
    }, [readError, deleteError]);

    const onClick = () => {
        markNotificationAsRead();

        const metadata = notification.metadata as
            | {
                  requestId?: string;
                  historicalImport?: { logId?: string };
              }
            | undefined;

        if (metadata?.historicalImport?.logId) {
            navigate(
                `/maintenance-tools?tab=historical-manual-override&logId=${metadata.historicalImport.logId}`,
            );
        } else {
            navigate(
                metadata?.requestId
                    ? `/price-change/requests?requestId=${metadata.requestId}`
                    : '/price-change/requests',
            );
        }

        close();
    };

    const onDeleteClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        removeNotification(notification.id);
        deleteNotification();
        event.stopPropagation();
    };

    return (
        <div className="group flex flex-col p-4 gap-4 hover:bg-gray-50">
            <UnstyledButton onClick={onClick}>
                <div
                    className={`rounded border-solid border-0 border-l-4 pl-2 ${
                        notification.read
                            ? 'border-transparent'
                            : 'border-primary'
                    }`}
                >
                    <Flex justify="space-between">
                        <Text className="whitespace-nowrap text-ellipsis max-w-full text-xs mb-2">
                            {formatDate(new Date(notification.sentAt))}
                        </Text>
                        <ActionIcon
                            color="red"
                            radius="xl"
                            variant="light"
                            size="sm"
                            className="hidden group-hover:flex"
                            onClick={onDeleteClick}
                        >
                            <IconX size="16" />
                        </ActionIcon>
                    </Flex>

                    <div className="flex">
                        <div
                            className={`rounded-full ${
                                !notification.read
                                    ? 'bg-primary/20 text-primary'
                                    : 'bg-slate-50 text-slate-300'
                            } flex items-center justify-center text-primary w-6 h-6 p-1`}
                        >
                            <IconTag />
                        </div>
                        <div className="flex flex-col gap-1 pl-3 overflow-hidden flex-1">
                            <div className="flex flex-1 justify-between gap-2">
                                <Text
                                    className={`${
                                        !notification.read
                                            ? 'uppercase font-bold text-primary'
                                            : 'font-normal'
                                    }`}
                                >
                                    {notification.subject}
                                </Text>
                            </div>
                            <Text className="text-sm max-w-full">
                                {notification.message}
                            </Text>
                        </div>
                    </div>

                    <UnstyledButton className="flex items-center gap-2 justify-end cursor-pointer w-min ml-auto">
                        <div className="text-sm text-primary">View</div>
                        <IconArrowRight className="text-primary" size={20} />
                    </UnstyledButton>
                </div>
            </UnstyledButton>
        </div>
    );
}

export default NotificationItem;
